'use client';

/**
 * @file AuctionList.tsx
 * @description 경매 리스트 컴포넌트 (Client Component)
 *
 * DB에서 이미 정렬 및 필터링이 완료된 경매 데이터를 표시합니다.
 * 클라이언트에서는 추가 정렬/필터링 없이 받은 데이터를 그대로 표시합니다.
 *
 * @see {@link /app/list/page.tsx} - 사용되는 페이지
 * @see {@link AuctionPreviewSheet} - 연결된 미리보기 시트
 */

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { AuctionItem } from './AuctionItem';
import { AuctionPreviewSheet } from './AuctionPreviewSheet';
import { AuctionFilters } from '@/lib/core/types';
import { RealAuction } from '@/lib/core/types';

interface AuctionListProps {
  auctions: RealAuction[];
  /** @deprecated DB에서 이미 필터링/정렬이 완료되어 사용하지 않음 (타입 호환성을 위해 유지) */
  filters?: AuctionFilters;
}

/**
 * 경매 리스트 컴포넌트
 *
 * DB에서 이미 정렬 및 필터링이 완료된 경매 데이터를 표시합니다.
 * 클라이언트에서는 추가 처리 없이 받은 데이터를 그대로 렌더링합니다.
 */
export function AuctionList({ auctions }: AuctionListProps) {
  const router = useRouter();

  // 미리보기 시트 상태 관리
  const [selectedAuction, setSelectedAuction] = useState<RealAuction | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleAuctionClick = (auction: RealAuction) => {
    setSelectedAuction(auction);
    setIsSheetOpen(true);
  };

  const handleSimulateClick = () => {
    // 분석 페이지로 이동
    if (selectedAuction) {
      const encodedCaseNo = encodeURIComponent(selectedAuction.caseNo);
      const encodedDisposalSeq = encodeURIComponent(selectedAuction.disposalSeq);
      router.push(`/analysis/${encodedCaseNo}/${encodedDisposalSeq}`);
    }
    setIsSheetOpen(false);
  };

  if (auctions.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-3 sm:mb-4">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <p className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400">해당 조건의 경매 물건이 없습니다.</p>
        <p className="text-xs sm:text-sm text-zinc-400 dark:text-zinc-500 mt-1.5 sm:mt-2">필터 조건을 변경해보세요.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 sm:gap-4">
        {auctions.map((auction) => (
          <AuctionItem
            key={`${auction.caseNo}-${auction.disposalSeq}`}
            auction={auction}
            viewCount={undefined} // 클라이언트에서 랜덤 값 생성하여 hydration mismatch 방지
            onClick={handleAuctionClick}
          />
        ))}
      </div>

      {/* 미리보기 시트 */}
      <AuctionPreviewSheet
        auction={selectedAuction}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onSimulateClick={handleSimulateClick}
      />
    </>
  );
}
